// Container clocks (13 §13.1, §13.2): the expensive deadlines of a container's journey, evaluated
// from its actual dates and free-time terms. Pure: callers pass `now` and calendars.
//
// The owner's goal is zero storage and zero demurrage: at most 4 working days at the port,
// at risk from day 2, directors from day 3. The system does not compute tariff penalties;
// it counts days over the free time and, when daily rates were entered, the money burning.
import {
  earliestDueAt,
  evaluateClock,
  nextDueAt,
  type Calendar,
  type ClockEvaluation,
  type ClockInput,
  type ClockPolicy,
  type ClockState,
  type ClockTarget,
  type Instant,
} from './clocks';
import { parseInstant } from './time';
import type { ContainerKind, ContainerStatus } from './movement';

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * The keys of `CLOCK_KEYS` in `@ecobox/schema` plus `demurrage`, which 13 §13.1 lists as its own
 * clock next to port free time (a test in the functions package keeps the two lists in step).
 */
export const CLOCK_KEY_LIST = [
  'origin_detention',
  'origin_consolidation',
  'port_cutoff',
  'transit',
  'bl_release',
  'destination_funding',
  'port_free_time',
  'demurrage',
  'customs_clearance',
  'empty_return',
  'devanning',
  'waiting_payment',
  'origin_dwell',
  'destination_dwell',
  'partner_leg',
  'no_update',
  'door_to_door',
  'pickup_unscheduled',
  'run_not_started',
  'stop_failed_twice',
  'driver_cash_overnight',
  'exception_open',
  'ticket_open',
  'day_not_closed',
  'einvoice_report',
] as const;
export type ClockKey = (typeof CLOCK_KEY_LIST)[number];

export type CostKind = 'storage' | 'demurrage' | 'detention';

export interface ClockDefault {
  readonly label: string;
  readonly target: ClockTarget;
  /** Role that gets the alert first. */
  readonly ownerRole: string;
  /** Roles above the owner, in order: the manager, then the directors (13 §13.2). */
  readonly escalation: readonly string[];
}

const hours = (amount: number): ClockTarget => ({ kind: 'calendar_hours', amount });
const days = (amount: number): ClockTarget => ({ kind: 'working_days', amount });
const UP = ['branch_manager', 'director'] as const;

/** Starting targets and owners. `slaPolicies` and a container's own terms override them. */
export const CLOCK_DEFAULTS: Readonly<Record<ClockKey, ClockDefault>> = {
  origin_detention: {
    label: 'Origin detention',
    target: hours(7 * 24),
    ownerRole: 'warehouse',
    escalation: UP,
  },
  origin_consolidation: {
    label: 'Origin consolidation',
    target: hours(10 * 24),
    ownerRole: 'warehouse',
    escalation: UP,
  },
  port_cutoff: { label: 'Port cut-off', target: hours(24), ownerRole: 'warehouse', escalation: UP },
  transit: {
    label: 'Transit',
    target: hours(24),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  bl_release: {
    label: 'BL / telex release',
    target: hours(5 * 24),
    ownerRole: 'branch_manager',
    escalation: ['director'],
  },
  destination_funding: {
    label: 'Destination funding',
    target: hours(24),
    ownerRole: 'group_finance',
    escalation: ['director'],
  },
  port_free_time: {
    label: 'Port free time',
    target: days(4),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  demurrage: {
    label: 'Demurrage',
    target: days(4),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  customs_clearance: {
    label: 'Customs clearance',
    target: days(3),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  empty_return: {
    label: 'Empty return',
    target: hours(7 * 24),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  devanning: { label: 'Devanning', target: hours(24), ownerRole: 'warehouse', escalation: UP },
  waiting_payment: {
    label: 'Waiting for payment',
    target: hours(72),
    ownerRole: 'cashier',
    escalation: UP,
  },
  origin_dwell: {
    label: 'Origin dwell',
    target: hours(10 * 24),
    ownerRole: 'warehouse',
    escalation: UP,
  },
  destination_dwell: {
    label: 'Destination dwell',
    target: days(2),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  partner_leg: {
    label: 'Partner leg',
    target: days(8),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  no_update: {
    label: 'No update from partner',
    target: hours(48),
    ownerRole: 'partner_coordinator',
    escalation: UP,
  },
  door_to_door: {
    label: 'Door to door',
    target: hours(45 * 24),
    ownerRole: 'branch_manager',
    escalation: ['director'],
  },
  pickup_unscheduled: {
    label: 'Pickup not scheduled',
    target: hours(24),
    ownerRole: 'dispatcher',
    escalation: UP,
  },
  run_not_started: {
    label: 'Run not started',
    target: hours(10),
    ownerRole: 'dispatcher',
    escalation: UP,
  },
  stop_failed_twice: {
    label: 'Stop failed twice',
    target: hours(24),
    ownerRole: 'dispatcher',
    escalation: UP,
  },
  driver_cash_overnight: {
    label: 'Driver cash held overnight',
    target: hours(24),
    ownerRole: 'cashier',
    escalation: UP,
  },
  exception_open: {
    label: 'Exception open',
    target: hours(24),
    ownerRole: 'branch_manager',
    escalation: ['director'],
  },
  ticket_open: {
    label: 'Feedback ticket open',
    target: hours(24),
    ownerRole: 'customer_support',
    escalation: UP,
  },
  day_not_closed: {
    label: 'Day not closed',
    target: hours(10),
    ownerRole: 'sector_accountant',
    escalation: UP,
  },
  // ZATCA: a simplified invoice is reported within 24 hours of issue (06 §6.8.4). The alert is
  // raised when that deadline is near and not met.
  einvoice_report: {
    label: 'E-invoice not reported',
    target: hours(24),
    ownerRole: 'sector_accountant',
    escalation: ['group_finance', 'director'],
  },
};

/** What a `slaPolicies` record may change about a clock. */
export interface ClockOverride {
  readonly target?: ClockTarget | undefined;
  /** Working days or hours elapsed before the clock is at risk (same unit as the target). */
  readonly atRiskAfter?: number | undefined;
  readonly escalateAfter?: number | undefined;
  readonly ownerRole?: string | undefined;
  readonly escalation?: readonly string[] | undefined;
}
export type ClockOverrides = Partial<Record<ClockKey, ClockOverride>>;

// --------------------------------------------------------------- calendars

/** Midnight at the start of `YYYY-MM-DD` in the calendar's timezone. */
export function startOfLocalDate(date: string, calendar: Calendar): number {
  return Date.parse(`${date}T00:00:00Z`) - calendar.timezoneOffsetMinutes * MINUTE_MS;
}

/** The local `YYYY-MM-DD` of an instant. */
export function localDate(instant: Instant, calendar: Calendar): string {
  return new Date(parseInstant(instant) + calendar.timezoneOffsetMinutes * MINUTE_MS)
    .toISOString()
    .slice(0, 10);
}

// ------------------------------------------------------------ one clock

function wholeDays(value: number | undefined, max: number): number | undefined {
  return value === undefined ? undefined : Math.min(max, Math.max(1, Math.floor(value)));
}

function policyOf(target: ClockTarget, override: ClockOverride | undefined): ClockPolicy {
  if (target.kind === 'working_days') {
    return {
      target,
      atRiskAfterWorkingDays: wholeDays(override?.atRiskAfter, target.amount),
      escalateAfterWorkingDays: wholeDays(override?.escalateAfter, target.amount),
    };
  }
  const after = override?.atRiskAfter;
  const ratio = after === undefined ? undefined : 1 - after / target.amount;
  return {
    target,
    atRiskRatio: ratio !== undefined && ratio > 0 && ratio < 1 ? ratio : undefined,
  };
}

export interface NamedClockInput {
  readonly key: ClockKey;
  readonly startedAt: Instant;
  readonly stoppedAt?: Instant | undefined;
  readonly now: Instant;
  readonly calendar: Calendar;
  /** Replaces the default target (a container's free days, a route target). */
  readonly target?: ClockTarget | undefined;
  readonly override?: ClockOverride | undefined;
}

function clockInput(input: NamedClockInput): ClockInput {
  const target = input.override?.target ?? input.target ?? CLOCK_DEFAULTS[input.key].target;
  return {
    ...policyOf(target, input.override),
    startedAt: input.startedAt,
    stoppedAt: input.stoppedAt,
    calendar: input.calendar,
    now: input.now,
  };
}

/** Evaluates any clock of the table with its default target, a given one, or a policy's. */
export function evaluateNamedClock(input: NamedClockInput): ClockEvaluation {
  return evaluateClock(clockInput(input));
}

export function nextChangeOfNamedClock(input: NamedClockInput): Date | null {
  return nextDueAt(clockInput(input));
}

// --------------------------------------------------------- container facts

export interface ContainerFreeTime {
  readonly portFreeDays: number;
  readonly demurrageFreeDays: number;
  readonly detentionFreeDays: number;
  readonly countWorkingDays: boolean;
  readonly detentionStartsAt: 'pull_out' | 'gate_out_full';
  readonly dailyRates: {
    readonly storageMinor?: number | undefined;
    readonly demurrageMinor?: number | undefined;
    readonly detentionMinor?: number | undefined;
    readonly currency?: string | undefined;
  };
}

export interface ContainerDates {
  readonly emptyPickedUp?: string | undefined;
  readonly gateInAtPort?: string | undefined;
  readonly closed?: string | undefined;
  readonly atd?: string | undefined;
  readonly blReleased?: string | undefined;
  readonly ata?: string | undefined;
  readonly discharged?: string | undefined;
  readonly customsLodged?: string | undefined;
  readonly customsReleased?: string | undefined;
  readonly pulledOut?: string | undefined;
  readonly arrivedAtHub?: string | undefined;
  readonly devanned?: string | undefined;
  readonly emptyReturned?: string | undefined;
}

export interface ContainerClockFacts {
  readonly kind: ContainerKind;
  readonly status: ContainerStatus;
  /** When the container entered its current status. Consolidation counts from it while loading. */
  readonly stateSince: string;
  readonly etd?: string | undefined;
  readonly eta?: string | undefined;
  readonly dates: ContainerDates;
  readonly freeTime: ContainerFreeTime;
  /** The destination-cost advance, once `closeContainer` raised it. */
  readonly advance?:
    | {
        readonly requestedAt: string;
        readonly dueDate: string;
        readonly fundedAt?: string | undefined;
      }
    | undefined;
}

export interface ContainerCalendars {
  readonly origin: Calendar;
  readonly destination: Calendar;
}

export interface ContainerClock {
  readonly key: ClockKey;
  readonly label: string;
  readonly startedAt: string;
  readonly stoppedAt?: string | undefined;
  readonly state: ClockState;
  readonly dueAt: string;
  /** The last day that is still free, `YYYY-MM-DD` in the clock's calendar. */
  readonly lastFreeDay: string;
  readonly unit: 'working_days' | 'ms';
  readonly remaining: number;
  readonly elapsed: number;
  readonly pastDue: boolean;
  /** The clock's own director escalation point has passed (port: day 3). */
  readonly escalate: boolean;
  /** The next moment this clock turns at risk, breaches or escalates. Null when nothing is left to turn. */
  readonly nextChangeAt: string | null;
  readonly ownerRole: string;
  readonly escalation: readonly string[];
  readonly costKind?: CostKind | undefined;
  /** Days (working or calendar) past the last free day, frozen when the clock stopped. */
  readonly daysOver: number;
  readonly perDayMinor?: number | undefined;
  readonly currency?: string | undefined;
  /** `daysOver × perDayMinor`: what the delay has cost so far, when daily rates are known. */
  readonly burnedMinor: number;
}

interface Window {
  readonly startMs: number;
  readonly stopMs?: number | undefined;
}

const ms = (value: string | undefined): number | undefined =>
  value === undefined ? undefined : parseInstant(value);

/** The free days of a clock: working days at the port, or plain calendar days. */
function freeDaysTarget(days: number, working: boolean): ClockTarget {
  const whole = Math.max(1, days);
  return working
    ? { kind: 'working_days', amount: whole }
    : { kind: 'calendar_hours', amount: whole * 24 };
}

/**
 * Half the free days to at risk, one before the end to the directors: 4 days → 2 and 3
 * (13 §13.1). Calendar clocks count the same in hours.
 */
function derivedFreeTime(
  days: number,
  working: boolean,
): { atRiskAfter: number; escalateAfter: number } {
  const whole = Math.max(1, days);
  const unit = working ? 1 : 24;
  return {
    atRiskAfter: Math.max(1, Math.floor(whole / 2)) * unit,
    escalateAfter: Math.max(1, whole - 1) * unit,
  };
}

/** Hours from a start to a due moment, never less than an hour so the clock is always valid. */
function hoursBetween(startMs: number, dueMs: number): number {
  return Math.max(1, Math.ceil((dueMs - startMs) / HOUR_MS));
}

/**
 * Every clock that applies to this container right now (13 §13.1), with state, last free day,
 * who owns it and the money burning. A clock appears once its start is known.
 */
export function evaluateContainerClocks(
  facts: ContainerClockFacts,
  calendars: ContainerCalendars,
  now: Instant,
  overrides: ClockOverrides = {},
): ContainerClock[] {
  const { kind, dates, freeTime } = facts;
  const working = freeTime.countWorkingDays;
  const fcl = kind === 'sea_fcl';
  const out: ContainerClock[] = [];

  const add = (
    key: ClockKey,
    window: Window,
    calendar: Calendar,
    options: {
      target: ClockTarget;
      /** The container's own free days: they win over a generic policy's target. */
      freeDays?: number | undefined;
      cost?: { kind: CostKind; rate: number | undefined } | undefined;
    },
  ): void => {
    // Demurrage has no policy record of its own yet: it follows the port free time policy.
    const policy = overrides[key] ?? (key === 'demurrage' ? overrides.port_free_time : undefined);
    const defaults = CLOCK_DEFAULTS[key];
    const derived =
      options.freeDays === undefined ? undefined : derivedFreeTime(options.freeDays, working);
    const target = derived ? options.target : (policy?.target ?? options.target);
    // A policy's own at-risk and escalation points are in its target's unit: ignore them if the
    // container counts days the other way.
    const sameUnit = (policy?.target?.kind ?? defaults.target.kind) === target.kind;
    const override: ClockOverride = {
      atRiskAfter: (sameUnit ? policy?.atRiskAfter : undefined) ?? derived?.atRiskAfter,
      escalateAfter: (sameUnit ? policy?.escalateAfter : undefined) ?? derived?.escalateAfter,
      ownerRole: policy?.ownerRole,
      escalation: policy?.escalation,
    };
    const startedAt = new Date(window.startMs).toISOString();
    const stoppedAt =
      window.stopMs === undefined ? undefined : new Date(window.stopMs).toISOString();
    const input: NamedClockInput = { key, startedAt, stoppedAt, now, calendar, target, override };
    const ev = evaluateNamedClock(input);
    const daysOver = ev.pastDue
      ? ev.unit === 'working_days'
        ? Math.max(1, -ev.remaining)
        : Math.max(1, Math.ceil(-ev.remaining / DAY_MS))
      : 0;
    const perDay = options.cost?.rate;
    const next = nextChangeOfNamedClock(input);
    out.push({
      key,
      label: defaults.label,
      startedAt,
      stoppedAt,
      state: ev.state,
      dueAt: ev.dueAt.toISOString(),
      lastFreeDay: localDate(ev.dueAt.getTime() - 1, calendar),
      unit: ev.unit,
      remaining: ev.remaining,
      elapsed: ev.elapsed,
      pastDue: ev.pastDue,
      escalate: ev.escalate,
      nextChangeAt: next === null ? null : next.toISOString(),
      ownerRole: override.ownerRole ?? defaults.ownerRole,
      escalation: override.escalation ?? defaults.escalation,
      costKind: options.cost?.kind,
      daysOver,
      perDayMinor: perDay,
      currency: freeTime.dailyRates.currency,
      burnedMinor: perDay === undefined ? 0 : daysOver * perDay,
    });
  };

  const gateIn = ms(dates.gateInAtPort);
  const atd = ms(dates.atd);
  const ata = ms(dates.ata);
  const discharge = ms(dates.discharged) ?? ata;
  const pulledOut = ms(dates.pulledOut);
  const arrivedAtHub = ms(dates.arrivedAtHub);
  const rates = freeTime.dailyRates;

  // ---- origin side
  if (fcl && dates.emptyPickedUp !== undefined) {
    add(
      'origin_detention',
      { startMs: parseInstant(dates.emptyPickedUp), stopMs: gateIn },
      calendars.origin,
      {
        target: freeDaysTarget(freeTime.detentionFreeDays, working),
        freeDays: freeTime.detentionFreeDays,
        cost: { kind: 'detention', rate: rates.detentionMinor },
      },
    );
  }
  if (facts.status === 'loading') {
    add(
      'origin_consolidation',
      { startMs: parseInstant(facts.stateSince), stopMs: ms(dates.closed) },
      calendars.origin,
      { target: CLOCK_DEFAULTS.origin_consolidation.target },
    );
  }
  if (fcl && dates.closed !== undefined && facts.etd !== undefined) {
    const closed = parseInstant(dates.closed);
    add('port_cutoff', { startMs: closed, stopMs: gateIn ?? atd }, calendars.origin, {
      target: hours(hoursBetween(closed, startOfLocalDate(facts.etd, calendars.origin))),
    });
  }

  // ---- in transit
  if (atd !== undefined && facts.eta !== undefined) {
    add('transit', { startMs: atd, stopMs: ata }, calendars.destination, {
      target: hours(hoursBetween(atd, startOfLocalDate(facts.eta, calendars.destination) + DAY_MS)),
    });
  }
  if (kind !== 'air' && facts.eta !== undefined) {
    const etaStart = startOfLocalDate(facts.eta, calendars.destination);
    add(
      'bl_release',
      { startMs: etaStart - 5 * DAY_MS, stopMs: ms(dates.blReleased) },
      calendars.destination,
      { target: hours(5 * 24) },
    );
  }
  if (facts.advance !== undefined) {
    const requested = parseInstant(facts.advance.requestedAt);
    const due = startOfLocalDate(facts.advance.dueDate, calendars.origin) + DAY_MS;
    // It stops when the advance is funded or the container leaves: a container only leaves
    // unfunded with a director's override (D-17), and from then on the cash calendar's shortfall
    // alert follows the money the destination still needs.
    const stops = [ms(facts.advance.fundedAt), atd].filter((t): t is number => t !== undefined);
    add(
      'destination_funding',
      { startMs: requested, stopMs: stops.length > 0 ? Math.min(...stops) : undefined },
      calendars.origin,
      { target: hours(hoursBetween(requested, due)) },
    );
  }

  // ---- at the port
  const storageStop = fcl ? pulledOut : arrivedAtHub;
  if (discharge !== undefined) {
    add('port_free_time', { startMs: discharge, stopMs: storageStop }, calendars.destination, {
      target: freeDaysTarget(freeTime.portFreeDays, working),
      freeDays: freeTime.portFreeDays,
      cost: { kind: 'storage', rate: rates.storageMinor },
    });
    if (fcl) {
      add('demurrage', { startMs: discharge, stopMs: pulledOut }, calendars.destination, {
        target: freeDaysTarget(freeTime.demurrageFreeDays, working),
        freeDays: freeTime.demurrageFreeDays,
        cost: { kind: 'demurrage', rate: rates.demurrageMinor },
      });
    }
  }
  if (dates.customsLodged !== undefined) {
    add(
      'customs_clearance',
      { startMs: parseInstant(dates.customsLodged), stopMs: ms(dates.customsReleased) },
      calendars.destination,
      { target: CLOCK_DEFAULTS.customs_clearance.target },
    );
  } else if (discharge !== undefined) {
    // Customs not started: the same clock runs from the arrival, so a box nobody lodged an entry
    // for shows up in the alerts and the digest (container watch). It stops when customs is
    // released or the container leaves the port.
    add(
      'customs_clearance',
      { startMs: discharge, stopMs: ms(dates.customsReleased) ?? pulledOut ?? arrivedAtHub },
      calendars.destination,
      { target: CLOCK_DEFAULTS.customs_clearance.target },
    );
  }

  // ---- empty container and devanning
  if (fcl && pulledOut !== undefined) {
    add(
      'empty_return',
      { startMs: pulledOut, stopMs: ms(dates.emptyReturned) },
      calendars.destination,
      {
        target: freeDaysTarget(freeTime.detentionFreeDays, working),
        freeDays: freeTime.detentionFreeDays,
        cost: { kind: 'detention', rate: rates.detentionMinor },
      },
    );
  }
  if (arrivedAtHub !== undefined) {
    add('devanning', { startMs: arrivedAtHub, stopMs: ms(dates.devanned) }, calendars.destination, {
      target: CLOCK_DEFAULTS.devanning.target,
    });
  }
  return out;
}

/** The earliest moment any running clock of the container changes: the watcher's `nextDueAt`. */
export function containerNextDueAt(clocks: readonly ContainerClock[]): Date | null {
  return earliestDueAt(
    clocks.map((c) => (c.nextChangeAt === null ? null : new Date(c.nextChangeAt))),
  );
}
